import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { api } from '../../services/api';
import { WorkOrder } from '../../services/types';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { CompactTable } from '../../components/ui/CompactTable';
import { TableFilterBar } from '../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../hooks/useTableFilterState';
import { FlowEntryBanner } from '../../components/workflow/FlowEntryBanner';
import {
    getOperatorWorkOrderLabel,
    getWorkOrderDescriptionSummary,
    getWorkOrderSearchText,
} from './workOrderDisplay';

interface ProductionMetrics {
    totalOrders: number;
    inProgress: number;
    completed: number;
    avgDuration: number; // hours
}

export function ProductionTracking() {
    const navigate = useNavigate();
    const [orders, setOrders] = useState<WorkOrder[]>([]);
    const [metrics, setMetrics] = useState<ProductionMetrics>({
        totalOrders: 0,
        inProgress: 0,
        completed: 0,
        avgDuration: 0
    });
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState<'timeline' | 'table'>('timeline');
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({ q: '' });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const woList = await api.manufacturing.listWorkOrders();
            setOrders(woList);
            calculateMetrics(woList);
        } catch (error) {
            console.error("Failed to load production data:", error);
        } finally {
            setLoading(false);
        }
    };

    const calculateMetrics = (woList: WorkOrder[]) => {
        const inProgress = woList.filter(w => w.status === 'In-Progress' || w.status === 'InProgress').length;
        const completed = woList.filter(w => w.status === 'Completed').length;

        // Calculate average duration for completed orders
        let totalDuration = 0;
        let completedCount = 0;
        woList.forEach(wo => {
            if (wo.start_date && wo.end_date && wo.status === 'Completed') {
                const start = new Date(wo.start_date).getTime();
                const end = new Date(wo.end_date).getTime();
                totalDuration += (end - start) / (1000 * 60 * 60); // Convert to hours
                completedCount++;
            }
        });

        setMetrics({
            totalOrders: woList.length,
            inProgress,
            completed,
            avgDuration: completedCount > 0 ? totalDuration / completedCount : 0
        });
    };

    const getWorkOrderCompletionPercent = (order: WorkOrder) => {
        return Math.max(0, Math.min(100, Number(order.progress_percent || 0)));
    };

    const getWorkOrderCompletionLabel = (order: WorkOrder) => {
        const produced = Number(order.produced_qty || 0);
        const target = Number(order.quantity || 0);
        const progress = getWorkOrderCompletionPercent(order);
        return `${produced.toFixed(2)} / ${target.toFixed(2)} (${progress.toFixed(0)}%)`;
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Draft':
            case 'Planned':
                return 'bg-gray-100 text-gray-700';
            case 'In-Progress':
            case 'InProgress':
                return 'bg-blue-100 text-blue-700';
            case 'Completed':
                return 'bg-green-100 text-green-700';
            default:
                return 'bg-slate-100 text-slate-700';
        }
    };

    const tableColumns = [
        { header: "WO ID", accessor: (order: WorkOrder) => `#${order.wo_id}`, className: 'font-medium text-slate-900' },
        {
            header: "Work Order",
            accessor: (order: WorkOrder) => {
                return (
                    <div className="min-w-[180px]">
                        <div className="font-medium text-slate-900">{getOperatorWorkOrderLabel(order)}</div>
                        <div className="text-xs text-slate-500 truncate" title={getWorkOrderDescriptionSummary(order)}>
                            {getWorkOrderDescriptionSummary(order)}
                        </div>
                    </div>
                );
            },
            className: 'text-slate-700' 
        },
        { header: "Qty", accessor: (order: WorkOrder) => (order.quantity || 0).toFixed(2), className: 'text-slate-700' },
        {
            header: "Sub-parts",
            accessor: (order: WorkOrder) => (
                <div className="text-xs text-slate-600 min-w-[120px]">
                    <div className="font-medium text-slate-800">{Number(order.child_count || 0)} sub-parts</div>
                    <div>{Number(order.active_child_count || 0)} active | {Number(order.pending_child_count || 0)} pending</div>
                </div>
            ),
            className: ''
        },
        { 
            header: "Status", 
            accessor: (order: WorkOrder) => (
                <span className={cn("px-2 py-1 rounded text-xs font-medium", getStatusColor(order.status))}>
                    {order.status}
                </span>
            ), 
            className: '' 
        },
        { header: "Start Date", accessor: (order: WorkOrder) => formatDate(order.start_date), className: 'text-slate-700' },
        { header: "End Date", accessor: (order: WorkOrder) => formatDate(order.end_date), className: 'text-slate-700' },
        { header: "Cost", accessor: (order: WorkOrder) => `$${(order.production_cost || order.total_cost || 0).toFixed(2)}`, className: 'text-slate-900 font-medium' },
        { 
            header: "WO Completion", 
            accessor: (order: WorkOrder) => {
                const progress = getWorkOrderCompletionPercent(order);
                return (
                    <div className="min-w-[150px]">
                        <div className="w-full bg-slate-100 rounded h-1.5">
                            <div
                                className={cn(
                                    "h-full rounded transition-all",
                                    order.status === 'Completed' ? 'bg-green-500' :
                                        order.status === 'In-Progress' || order.status === 'InProgress' ? 'bg-blue-500' :
                                            'bg-gray-400'
                                )}
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        <div className="text-xs text-slate-600 mt-1">{getWorkOrderCompletionLabel(order)}</div>
                    </div>
                );
            }, 
            className: '' 
        },
    ];
    const filteredOrders = useMemo(() => {
        const query = state.q.trim().toLowerCase();
        if (!query) return orders;
        return orders.filter((order) => {
            return (
                String(order.wo_id).includes(query) ||
                getWorkOrderSearchText(order).includes(query) ||
                getOperatorWorkOrderLabel(order).toLowerCase().includes(query)
            );
        });
    }, [orders, state.q]);

    if (loading) {
        return <div className="p-8 text-center text-slate-500">Loading production data...</div>;
    }

    return (
        <div className="space-y-6 pb-8">
            <FlowEntryBanner
                title="Production Tracking Guidance"
                pathLabel="Execution Monitoring"
                nextHint="Use this view to monitor WO completion and open WO detail for operational output, materials, and cost posting."
                helpModule="production"
            />
            {/* Header */}
            <div>
                <div className="flex justify-between items-center">
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">Production Tracking</h1>
                    <div className="flex gap-2">
                        <Button
                            onClick={() => setViewMode('timeline')}
                            variant={viewMode === 'timeline' ? 'default' : 'outline'}
                            size="sm"
                        >
                            Timeline
                        </Button>
                        <Button
                            onClick={() => setViewMode('table')}
                            variant={viewMode === 'table' ? 'default' : 'outline'}
                            size="sm"
                        >
                            Table
                        </Button>
                    </div>
                </div>
                <p className="text-muted-foreground">Monitor main work order completion and production status.</p>
            </div>

            {/* Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-slate-500 text-sm">Total Orders</p>
                            <p className="text-2xl font-bold text-slate-900">{metrics.totalOrders}</p>
                        </div>
                        <TrendingUp className="w-8 h-8 text-slate-300" />
                    </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-slate-500 text-sm">In Progress</p>
                            <p className="text-2xl font-bold text-blue-600">{metrics.inProgress}</p>
                        </div>
                        <Clock className="w-8 h-8 text-blue-300" />
                    </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-slate-500 text-sm">Completed</p>
                            <p className="text-2xl font-bold text-green-600">{metrics.completed}</p>
                        </div>
                        <CheckCircle className="w-8 h-8 text-green-300" />
                    </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-slate-500 text-sm">Avg Duration</p>
                            <p className="text-2xl font-bold text-slate-900">{metrics.avgDuration.toFixed(1)}h</p>
                        </div>
                        <Calendar className="w-8 h-8 text-slate-300" />
                    </div>
                </div>
            </div>

            {/* Timeline View */}
            {viewMode === 'timeline' && (
                <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
                    <h2 className="font-bold text-slate-900">Work Order Completion</h2>
                    {orders.length === 0 ? (
                        <p className="text-slate-500 text-center py-8">No work orders scheduled</p>
                    ) : (
                        <div className="space-y-3">
                            {orders.map((order) => {
                                const progress = getWorkOrderCompletionPercent(order);

                                return (
                                    <div key={order.wo_id} className="border border-slate-200 rounded p-4 hover:bg-slate-50 transition">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex-1">
                                                <p className="font-medium text-slate-900">{getOperatorWorkOrderLabel(order)}</p>
                                                <p className="text-xs text-slate-500">
                                                    {getWorkOrderDescriptionSummary(order)} • Qty: {(order.quantity || 0).toFixed(2)}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                                                    WO
                                                </span>
                                                <span className={cn(
                                                    "px-3 py-1 rounded-full text-xs font-medium",
                                                    getStatusColor(order.status)
                                                )}>
                                                    {order.status}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Completion Bar */}
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="text-xs text-slate-600 w-16">{formatDate(order.start_date)}</div>
                                            <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                                                <div
                                                    className={cn(
                                                        "h-full transition-all",
                                                        order.status === 'Completed' ? 'bg-green-500' :
                                                            order.status === 'In-Progress' || order.status === 'InProgress' ? 'bg-blue-500' :
                                                                'bg-gray-400'
                                                    )}
                                                    style={{ width: `${progress}%` }}
                                                />
                                            </div>
                                            <div className="text-xs text-slate-600 w-16 text-right">{formatDate(order.end_date)}</div>
                                        </div>

                                        {/* Details */}
                                        <div className="grid grid-cols-3 gap-4 text-xs">
                                            <div>
                                                <p className="text-slate-500">Due Date</p>
                                                <p className="font-medium text-slate-900">{formatDate(order.due_date)}</p>
                                            </div>
                                            <div>
                                                <p className="text-slate-500">Production Cost</p>
                                                <p className="font-medium text-slate-900">
                                                    ${(order.production_cost || order.total_cost || 0).toFixed(2)}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-slate-500">WO Completion</p>
                                                <p className="font-medium text-slate-900">{getWorkOrderCompletionLabel(order)}</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                    <div className="p-3 border-b">
                        <TableFilterBar
                            searchValue={state.q}
                            onSearchChange={(value) => setFilter('q', value)}
                            searchPlaceholder="Search work orders..."
                            isDirty={isDirty}
                            onClear={clearAll}
                            rightSlot={`${filteredOrders.length} results`}
                        />
                    </div>
                    <CompactTable
                        data={filteredOrders}
                        columns={tableColumns}
                        keyField="wo_id"
                        onRowClick={(row) => navigate(`/manufacturing/orders/${row.wo_id}`)}
                    />
                    <div className="px-3 pb-3 text-xs text-slate-500">
                        Tip: open a row from Work Orders tab to view lineage, prechecks, and completion actions.
                    </div>
                    {filteredOrders.length === 0 && (
                        <div className="text-center py-8 text-slate-500">No work orders found</div>
                    )}
                </div>
            )}
        </div>
    );
}
